import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import { shiftApi } from '../../../api/shiftApi';
import { toast } from 'react-toastify';
import {
  formatCurrentTime12Hour,
  formatTime12Hour,
  isValid12HourTime,
  isValidTimeValue,
} from '../../../utils/timeUtils';
import TwelveHourTimeField from './TwelveHourTimeField';

export type ShiftFormInitialValues = {
  id?: string;
  shiftName: string;
  startTime: string;
  endTime: string;
  breakDuration?: number;
  gracePeriod?: number;
  graceTime?: number;
  isActive?: boolean;
  isAssigned?: boolean;
};

type ShiftFormProps = {
  onSuccess?: () => void;
  showCard?: boolean;
  showTitle?: boolean;
  initialShift?: ShiftFormInitialValues | null;
  timeFieldsDisabled?: boolean;
};

const ShiftForm: React.FC<ShiftFormProps> = ({
  onSuccess,
  showCard = true,
  showTitle = true,
  initialShift = null,
  timeFieldsDisabled = false,
}) => {
  const [shiftName, setShiftName] = useState('');
  const [startTime, setStartTime] = useState(formatCurrentTime12Hour);
  const [endTime, setEndTime] = useState(formatCurrentTime12Hour);
  const [breakDuration, setBreakDuration] = useState('60');
  const [gracePeriod, setGracePeriod] = useState('15');
  const [submitting, setSubmitting] = useState(false);
  const isEditMode = Boolean(initialShift?.id);

  useEffect(() => {
    if (!initialShift) {
      setShiftName('');
      setStartTime(formatCurrentTime12Hour());
      setEndTime(formatCurrentTime12Hour());
      setBreakDuration('60');
      setGracePeriod('15');
      return;
    }

    setShiftName(initialShift.shiftName || '');
    setStartTime(formatTime12Hour(initialShift.startTime, ''));
    setEndTime(formatTime12Hour(initialShift.endTime, ''));
    setBreakDuration(String(initialShift.breakDuration ?? 60));
    setGracePeriod(String(initialShift.gracePeriod ?? initialShift.graceTime ?? 15));
  }, [initialShift]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!shiftName || !startTime || !endTime) {
      toast.error('Please fill all required fields');
      return;
    }
    const isValidStartTime = isValid12HourTime(startTime) || isValidTimeValue(startTime);
    const isValidEndTime = isValid12HourTime(endTime) || isValidTimeValue(endTime);

    if (!isValidStartTime || !isValidEndTime) {
      toast.error('Please enter start and end time like 10:00 and select AM/PM');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        shiftName,
        startTime,
        endTime,
        breakDuration: Number(breakDuration),
        gracePeriod: Number(gracePeriod),
        isActive: initialShift?.isActive,
      };

      if (isEditMode && initialShift?.id) {
        await shiftApi.updateShift(initialShift.id, payload);
      } else {
        await shiftApi.createShift(payload);
      }

      toast.success(isEditMode ? 'Shift updated successfully!' : 'Shift saved successfully!');

      if (!isEditMode) {
        setShiftName('');
        setStartTime(formatCurrentTime12Hour());
        setEndTime(formatCurrentTime12Hour());
        setBreakDuration('60');
        setGracePeriod('15');
      }
      onSuccess?.();
    } catch (err: any) {
      const responseData = err.response?.data;
      const modelErrors = responseData?.errors
        ? Object.values(responseData.errors).flat().join(', ')
        : '';
      toast.error(responseData?.message || modelErrors || 'Failed to save shift');
    } finally {
      setSubmitting(false);
    }
  };

  const content = (
    <>
      {showTitle && (
        <Typography variant="h6" fontWeight="bold" gutterBottom >
          {isEditMode ? 'Edit Shift' : 'Add New Shift'}
        </Typography>
      )}
      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          {isEditMode && timeFieldsDisabled && (
            <Grid size={12}>
              <Alert severity="info">
                This shift is assigned to employee(s). Only the shift name can be changed.
              </Alert>
            </Grid>
          )}
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              label="Shift Name *"
              fullWidth
              size="small"
              value={shiftName}
              onChange={(e) => setShiftName(e.target.value)}
              placeholder="e.g., Morning Shift, Night Shift"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TwelveHourTimeField
              label="Start Time"
              required
              value={startTime}
              onChange={setStartTime}
              disabled={timeFieldsDisabled}
              helperText={timeFieldsDisabled ? 'Locked because this shift is assigned' : undefined}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TwelveHourTimeField
              label="End Time"
              required
              value={endTime}
              onChange={setEndTime}
              disabled={timeFieldsDisabled}
              helperText={timeFieldsDisabled ? 'Locked because this shift is assigned' : undefined}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              label="Break Duration (minutes)"
              type="number"
              fullWidth
              size="small"
              value={breakDuration}
              onChange={(e) => setBreakDuration(e.target.value)}
              inputProps={{ min: 0 }}
              disabled={timeFieldsDisabled}
              helperText={timeFieldsDisabled ? 'Locked because this shift is assigned' : undefined}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              label="Grace Period (minutes)"
              type="number"
              fullWidth
              size="small"
              value={gracePeriod}
              onChange={(e) => setGracePeriod(e.target.value)}
              inputProps={{ min: 0 }}
              disabled={timeFieldsDisabled}
              helperText={timeFieldsDisabled ? 'Locked because this shift is assigned' : undefined}
            />
          </Grid>
        </Grid>

        <Button
          type="submit"
          variant="contained"
          color="primary"
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <Save />}
          sx={{ mt: 3 ,textTransform: 'none'}}
          disabled={submitting}
        >
          {submitting ? 'Saving...' : isEditMode ? 'Update Shift' : 'Save Shift'}
        </Button>
      </Box>
    </>
  );

  if (!showCard) {
    return content;
  }

  return (
    <Card>
      <CardContent>{content}</CardContent>
    </Card>
  );
};

export default ShiftForm;
